/**
 * Route Editor Page
 * Full-featured route editing with drag-and-drop
 */

import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import {
  RouteEditorPanel,
  StopDetailModal,
  UnassignedTasksPanel,
} from '../components/editor';
import { RouteMap } from '../components/routes';
import {
  useRouteRun,
  useReorderStops,
  useReassignTask,
  useTasks,
} from '../hooks/useApi';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import type { RouteStop } from '../types';

export default function RouteEditor() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  // State
  // null until the user toggles a route; the first route is expanded by default
  const [expandedRouteOverride, setExpandedRouteId] = useState<string | null>(null);
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [selectedStop, setSelectedStop] = useState<RouteStop | null>(null);
  const [stopsCache] = useState<Map<string, RouteStop[]>>(new Map());

  // Queries
  const { data: routeRunData, isLoading: isLoadingRun, refetch: refetchRun } = useRouteRun(runId || '');
  
  // Get date from route run for fetching unassigned tasks
  const date = routeRunData?.routeRun?.date;
  
  const { data: tasksData } = useTasks(date);

  // Mutations
  const reorderMutation = useReorderStops();
  const reassignMutation = useReassignTask();

  // Build stops map
  const stopsMap = useMemo(() => {
    // In production, we'd fetch stops for each route
    // For now, use the cache
    return stopsCache;
  }, [stopsCache]);

  // Auto-expand first route: derived during render instead of copied into state by an effect
  const expandedRouteId = expandedRouteOverride || routeRunData?.routes?.[0]?._id || null;

  // Handle reorder stops
  const handleReorderStops = useCallback(async (routeId: string, newOrder: string[]) => {
    try {
      await reorderMutation.mutateAsync({ routeId, newOrder });
      refetchRun();
    } catch (error) {
      console.error('Failed to reorder stops:', error);
      throw error;
    }
  }, [reorderMutation, refetchRun]);

  // Handle reassign task
  const handleReassignTask = useCallback(async (taskUid: string, toRouteId: string) => {
    try {
      await reassignMutation.mutateAsync({ taskUid, toRouteId });
      refetchRun();
    } catch (error) {
      console.error('Failed to reassign task:', error);
      throw error;
    }
  }, [reassignMutation, refetchRun]);

  // Handle stop click
  const handleStopClick = useCallback((stop: RouteStop) => {
    setSelectedStopId(stop._id);
    setSelectedStop(stop);
  }, []);

  // Handle assign unassigned task
  const handleAssignTask = useCallback(async (taskUid: string, routeId: string) => {
    await handleReassignTask(taskUid, routeId);
  }, [handleReassignTask]);

  // Get unassigned tasks
  const unassignedTasks = tasksData?.tasks || [];

  // Get available routes for assignment
  const availableRoutes = routeRunData?.routes?.map((r) => ({
    id: r._id,
    driverName: r.driver_name || r.driver_uid,
  })) || [];

  if (!runId) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">No route run selected</p>
        <Button variant="outline" onClick={() => navigate('/routes')} className="mt-4">
          Go to Routes
        </Button>
      </div>
    );
  }

  if (isLoadingRun) {
    return (
      <div className="h-[calc(100vh-3.5rem)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!routeRunData) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <p className="text-gray-900 font-medium">Route run not found</p>
        <Button variant="outline" onClick={() => navigate('/routes')} className="mt-4">
          Go to Routes
        </Button>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/routes')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Route Editor</h1>
            <p className="text-gray-500 text-sm">
              {routeRunData.routeRun.date} • {routeRunData.routes.length} routes • {routeRunData.routeRun.task_count} tasks
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(reorderMutation.isPending || reassignMutation.isPending) && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Unassigned Tasks */}
        <div className="w-72 border-r border-gray-200 bg-gray-50 overflow-hidden">
          <UnassignedTasksPanel
            tasks={unassignedTasks}
            onAssignTask={handleAssignTask}
            availableRouteIds={availableRoutes}
          />
        </div>

        {/* Middle Panel - Routes */}
        <div className="w-96 border-r border-gray-200 bg-white overflow-y-auto p-4 space-y-4">
          {routeRunData.routes.map((route) => (
            <RouteEditorPanel
              key={route._id}
              route={route}
              stops={stopsMap.get(route._id) || []}
              onReorderStops={handleReorderStops}
              onStopClick={handleStopClick}
              selectedStopId={selectedStopId || undefined}
              isExpanded={expandedRouteId === route._id}
              onToggleExpand={() => {
                setExpandedRouteId(
                  expandedRouteId === route._id ? null : route._id
                );
              }}
            />
          ))}
        </div>

        {/* Right Panel - Map */}
        <div className="flex-1 relative">
          <RouteMap
            routes={routeRunData.routes}
            stops={stopsMap}
            selectedRouteId={expandedRouteId || undefined}
            selectedStopId={selectedStopId || undefined}
            onStopSelect={handleStopClick}
            className="absolute inset-0"
          />
        </div>
      </div>

      {/* Stop Detail Modal */}
      <StopDetailModal
        stop={selectedStop}
        onClose={() => {
          setSelectedStop(null);
          setSelectedStopId(null);
        }}
        onReassign={(taskUid) => {
          // Open reassign dialog
          console.log('Reassign task:', taskUid);
        }}
      />
    </div>
  );
}
